// MUVTable.jsx
import React, { useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import "./MUVTable.css";
import { SnackbarContext } from "context/snackBarContext";
import { AuthData } from "auth/AuthWarpper";
import Service from "../../../services/ManageUnannouncedVisitorService";
import WarningModal from '../../../utils/WarningModal';
import dayjs from 'dayjs';

const MUVTable = ({ data, onAssignmentUpdate ,cropYear}) => {
    const { t } = useTranslation();
    const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
    const { user } = AuthData();
    const [loading, setLoading] = useState(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

    // State to track the checked status of the header checkbox and body checkboxes
    const [headerChecked, setHeaderChecked] = useState(false);
    const [checkedItems, setCheckedItems] = useState([]);
    // Add new state to track individual row assignment status
    const [rowAssignmentStatus, setRowAssignmentStatus] = useState({});
    // Add new helper function to check if user can unassign
    const canUnassign = (item) => {
        return !item.isAssigned || 
               (item.isAssigned && item.batOneId === user?.batOneId);
    };

    // New helper function to check if a row should be disabled
    const isRowDisabled = (item) => {
        const disabledStatuses = ['not started', 'inprogress', 're-opened'];
        return disabledStatuses.includes(item.surveyStatus?.toLowerCase());
    };

    // Update handleHeaderCheckboxChange function
    const handleHeaderCheckboxChange = async (event) => {
        if (loading) return; // Prevent multiple clicks while loading
        
        const checked = event.target.checked;
        setHeaderChecked(checked);

        // Get items based on conditions, excluding disabled rows
        const itemsToUpdate = data.filter(item => {
            // Exclude rows with disabled statuses
            if (isRowDisabled(item)) return false;

            if (checked) {
                // When checking: select unassigned items and items assigned to current user
                return !item.isAssigned || (item.isAssigned && item.batOneId === user?.batOneId);
            } else {
                // When unchecking: select all items that are either in rowAssignmentStatus or assigned to current user
                return (rowAssignmentStatus[item.growerNumber] === true) || 
                       (item.isAssigned && item.batOneId === user?.batOneId);
            }
        });

        if (itemsToUpdate.length === 0) return;

        try {
            setLoading(true);
            // Reset assignment status before making the API call
            const newAssignmentStatus = {};
            itemsToUpdate.forEach(item => {
                newAssignmentStatus[item.growerNumber] = checked;
            });
            setRowAssignmentStatus(newAssignmentStatus);

            // Create payload for all selected items
            const updatePayload = itemsToUpdate.map(item => ({
                muvId: item.muvId || null,
                batOneId: checked ? user?.batOneId : null,
                userName: checked ? user?.userName : null,
                growerNumber: item.growerNumber,
                cropYear: cropYear,
                propertyFarmNumber: item.propertyFarmNumber,
                farmerName: item.farmerName,
                isAssigned: checked,
                assignedDate: checked ? dayjs().format('YYYY-MM-DD') : null,
                unAssignedDate: !checked ? dayjs().format('YYYY-MM-DD') : null,
                createdByUser: user?.userName,
                modifiedByUser: user?.userName
            }));

            const response = await Service.updateVisitorAssignment(updatePayload);

            if (response?.data) {
                setSeverity("success");
                setMessage(checked ? "Successfully assigned visitors" : "Successfully unassigned visitors");
                setOpenAlert(true);
                onAssignmentUpdate?.(); // Notify parent component of updates if callback exists
            }
        } catch (error) {
            console.error("Error updating assignments:", error);
            setSeverity("error");
            setMessage(error?.response?.data?.message || error?.message);
            setOpenAlert(true);
            // Revert the assignment status on error
            const revertStatus = {};
            itemsToUpdate.forEach(item => {
                revertStatus[item.growerNumber] = !checked;
            });
            setRowAssignmentStatus(revertStatus);
            setHeaderChecked(!checked);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignment = async (growerNumber, isAssigning) => {
        try {
            const item = data.find(d => d.growerNumber === growerNumber);

            // Check if trying to unassign someone else's assignment
            if (!isAssigning && item.isAssigned && item.batOneId !== user?.batOneId) {
                setSeverity("warning");
                setMessage("The selected farmer is assigned to other unannounced visitor. Please contact the unannounced visitor to unassign the farmer or wait the expired time to the farmer be automatically unassigned.");
                setOpenAlert(true);
                return;
            }

            setLoading(true);
            const updatePayload = [{
                muvId: item.muvId || null,
                batOneId: isAssigning ? user?.batOneId : null,
                userName: isAssigning ? user?.userName : null,
                growerNumber: item.growerNumber,
                cropYear: cropYear,
                propertyFarmNumber: item.propertyFarmNumber,
                farmerName: item.farmerName,
                isAssigned: isAssigning,
                assignedDate: isAssigning ? dayjs().format('YYYY-MM-DD') : null,
                unAssignedDate: !isAssigning ? dayjs().format('YYYY-MM-DD') : null,
                createdByUser: user?.userName,
                modifiedByUser: user?.userName
            }];

            const response = await Service.updateVisitorAssignment(updatePayload);

            if (response?.data) {
                setSeverity("success");
                setMessage(isAssigning ? "Successfully assigned visitor" : "Successfully unassigned visitor");
                setOpenAlert(true);

                // Update local assignment status for this row only
                setRowAssignmentStatus(prev => ({
                    ...prev,
                    [growerNumber]: isAssigning
                }));
            }
        } catch (error) {
            console.error("Error updating assignment:", error);
            setSeverity("error");
            setMessage(error?.response?.data?.message || error?.message);
            setOpenAlert(true);
        } finally {
            setLoading(false);
        }
    };

    // Add or update handleRowCheckboxChange function
    const handleRowCheckboxChange = (event, growerNumber) => {
        const isChecked = event.target.checked;
        const item = data.find(d => d.growerNumber === growerNumber);

        // Check if trying to unassign someone else's assignment
        if (!isChecked && item.isAssigned && item.batOneId !== user?.batOneId) {
            setIsWarningModalOpen(true);
            return;
        }

        handleAssignment(growerNumber, isChecked);
    };

    // Update isHeaderChecked function
    const isHeaderChecked = () => {
        if (data.length === 0) return false;

        const relevantItems = data.filter(item => 
            !item.isAssigned || (item.isAssigned && item.batOneId === user?.batOneId)
        );

        // Check if all relevant items are either in rowAssignmentStatus or assigned to current user
        return relevantItems.length > 0 && relevantItems.every(item => 
            (rowAssignmentStatus[item.growerNumber] === true) || 
            (item.isAssigned && item.batOneId === user?.batOneId && rowAssignmentStatus[item.growerNumber] !== false)
        );
    };

    return (
        <>
            <div className="table-container">
                <table className="muv-table">
                    <thead>
                        <tr>
                            <th className="checkbox-header">
                                <Checkbox
                                    checked={isHeaderChecked()}
                                    onChange={handleHeaderCheckboxChange}
                                    disabled={loading}
                                    indeterminate={
                                        Object.values(rowAssignmentStatus).some(Boolean) && 
                                        !isHeaderChecked()
                                    }
                                />
                            </th>
                            <th style={{justifyContent : "flex-start"}}>
                                <div>
                                    {t('Property No.')}
                                </div>
                            </th>
                            <th style={{justifyContent : "flex-start"}}>
                                <div>
                                    {t('Grower No.')}
                                </div>
                            </th>
                            <th className="actions-header" style={{justifyContent : "flex-start"}}>
                                <div>
                                    {t('Farmer Name')}
                                </div>
                            </th>
                            <th className="actions-header" style={{justifyContent : "flex-start"}}>
                                <div>
                                    {t('Status')}
                                </div>
                            </th>
                            <th className="actions-header" style={{justifyContent : "flex-end"}}>
                                <div>
                                    {t('Unannounced Visitor Name')}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr 
                                key={index} 
                                className={isRowDisabled(item) ? 'disabled-row' : ''}
                                style={{ 
                                    
                                    pointerEvents: isRowDisabled(item) ? 'none' : 'auto'
                                }}
                            >
                                <td className="checkbox-cell">
                                    <Checkbox
                                        checked={
                                            rowAssignmentStatus[item.growerNumber] !== undefined 
                                                ? rowAssignmentStatus[item.growerNumber] 
                                                : item.isAssigned
                                        }
                                        onChange={(e) => handleRowCheckboxChange(e, item.growerNumber)}
                                        disabled={
                                            loading || 
                                            isRowDisabled(item)
                                        }
                                        title={
                                            isRowDisabled(item)
                                                ? "Cannot assign due to current survey status" 
                                                : (item.isAssigned && item.batOneId !== user?.batOneId 
                                                    ? "Assigned to another visitor" 
                                                    : "")
                                        }
                                    />
                                </td>
                                <td style={{justifyContent : "flex-start"}}>{item.propertyFarmNumber}</td>
                                <td style={{justifyContent : "flex-start"}}>{item.growerNumber}</td>
                                <td style={{justifyContent : "flex-start"}}>{item.farmerName}</td>
                                <td style={{justifyContent : "flex-start"}}>{item.surveyStatus}</td>
                                <td style={{justifyContent : "flex-end"}}>{item.userName || '-'}</td>
                            </tr>
                        ))}
                        {data.length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ textAlign: 'center' }}>
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {loading && (
                    <div style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)'
                    }}>
                        <CircularProgress />
                    </div>
                )}
            </div>
            <WarningModal 
                open={isWarningModalOpen}
                onClose={() => setIsWarningModalOpen(false)}
                message={`The selected farmer is assigned to other unannounced visitor. 
                    Please contact the unannounced visitor to unassign the farmer or 
                    wait the expired time to the farmer be automatically unassigned.`}
            />
        </>
    );
};

export default MUVTable;
